import { useSeoMeta } from "@unhead/react";
import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Rows3,
  Grid2x2,
  Columns3,
  Star,
  Copy,
  Grid2x2Plus,
  SquareSlash,
  EyeClosed,
} from "lucide-react";
import { Confetti } from "@/components/Confetti";
import { Skeleton } from "@/components/ui/skeleton";
import { ColorMomentCard } from "@/components/ColorMomentCard";
import { ColorPaletteDisplay } from "@/components/ColorPaletteDisplay";
import { CaptureModeSelector } from "@/components/CaptureModeSelector";
import { EmojiPicker } from "@/components/EmojiPicker";
import { WavyTransition } from "@/components/WavyTransition";
import { CurtainTransition } from "@/components/CurtainTransition";
import {
  useColorMomentsInfinite,
  useFollowingColorMomentsInfinite,
  type LayoutMode,
} from "@/hooks/useColorMoments";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useNostrPublish } from "@/hooks/useNostrPublish";
import { useToast } from "@/hooks/useToast";
import { LoginArea } from "@/components/auth/LoginArea";
import { useQueryClient } from "@tanstack/react-query";
import { getEmojiToast } from "@/lib/emojiToasts";
import { formatColorsForCopy } from "@/lib/utils";
import { getColorCopyToast } from "@/lib/copyToasts";
import { useScrollTimer } from "@/hooks/useScrollTimer";
import { useTransition } from "@/hooks/useTransition";
import { useLandscapeOverlay } from "@/hooks/useLandscapeOverlay";
import { TeaTimeOverlay } from "@/components/TeaTimeOverlay";
import { ColorCritic } from "@/components/ColorCritic";
import { ColorAlchemist } from "@/components/ColorAlchemist";
import { ColorAstrologer } from "@/components/ColorAstrologer";
import { usePostSubmissionEvent } from "@/hooks/usePostSubmissionEvent";
import { useUserColorMoments } from "@/hooks/useColorMoments";
import { useAuthor } from "@/hooks/useAuthor";
import { useInView } from "react-intersection-observer";

type FeedMode = "everyone" | "following";

const GREETINGS = ["Hello!", "Hey!", "Hi!", "Howdy!", "👋", "Yo!", "Hiya!"];

const LAYOUT_ICONS: Record<LayoutMode, typeof Rows3> = {
  horizontal: Rows3,
  grid: Grid2x2,
  vertical: Columns3,
  star: Star,
  checkerboard: Grid2x2Plus,
  diagonalStripes: SquareSlash,
};

/**
 * Get available layouts based on color count.
 * Grid is only available for 4 or 6 colors.
 * Star is not available for 4 colors (looks like a square/grid).
 * Checkerboard and diagonal stripes are always available.
 */
function getAvailableLayouts(colorCount: number): LayoutMode[] {
  const layouts: LayoutMode[] = ["horizontal", "vertical"];
  // Grid only works well with 4 or 6 colors
  if (colorCount === 4 || colorCount === 6) {
    layouts.push("grid");
  }
  // Star doesn't work well with 4 colors (just makes a square)
  if (colorCount !== 4) {
    layouts.push("star");
  }
  // Checkerboard and diagonal stripes are always available
  layouts.push("checkerboard", "diagonalStripes");
  return layouts;
}

const Index = () => {
  useSeoMeta({
    title: "Espy - See beauty, share color",
    description:
      "A quiet place to share the colors you witness in beautiful moments.",
  });

  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [startInCameraMode, setStartInCameraMode] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [selectedColors, setSelectedColors] = useState<string[]>([]);
  const [selectedLayout, setSelectedLayout] =
    useState<LayoutMode>("horizontal");
  const [selectedName, setSelectedName] = useState("");
  const [selectedEmoji, setSelectedEmoji] = useState("");
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [confettiColors, setConfettiColors] = useState<string[]>([]);
  const [criticColors, setCriticColors] = useState<string[] | undefined>(
    undefined,
  );
  const [alchemistColors, setAlchemistColors] = useState<string[] | undefined>(
    undefined,
  );
  const [astrologerEmoji, setAstrologerEmoji] = useState<string | undefined>(
    undefined,
  );
  const [astrologerColors, setAstrologerColors] = useState<
    string[] | undefined
  >(undefined);
  const rollPostEvent = usePostSubmissionEvent();
  const [showCurtainTransition, setShowCurtainTransition] = useState(false);
  const [showCurtainBack, setShowCurtainBack] = useState(false);
  const [pendingColors, setPendingColors] = useState<string[]>([]);
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [waterMotion, setWaterMotion] = useState(0);
  const [feedMode, setFeedMode] = useState<FeedMode>("everyone");
  const [isBlinking, setIsBlinking] = useState(false);
  const [isGreetingBlink, setIsGreetingBlink] = useState(false);
  const [showFeedLabel, setShowFeedLabel] = useState(false);
  const [feedLabelText, setFeedLabelText] = useState<"everyone" | "friends">(
    "everyone",
  );
  const [greeting] = useState(
    () => GREETINGS[Math.floor(Math.random() * GREETINGS.length)],
  );
  const [showGreeting, setShowGreeting] = useState(false);
  const [hasSeenGreeting, setHasSeenGreeting] = useState(() => {
    return sessionStorage.getItem("espy-greeting-seen") === "true";
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const touchStartY = useRef<number>(0);
  const waterAnimationRef = useRef<number | null>(null);
  const { user } = useCurrentUser();
  const { mutate: createEvent, isPending: isPublishing } = useNostrPublish();
  const isFollowingMode = feedMode === "following" && !!user;
  const everyoneQuery = useColorMomentsInfinite();
  const followingQuery = useFollowingColorMomentsInfinite(
    user?.pubkey,
    isFollowingMode,
  );
  const { data: userMoments } = useUserColorMoments(user?.pubkey || "");
  const userAuthor = useAuthor(user?.pubkey);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { playTransition } = useTransition();
  const { showRotatePrompt, unsuppress } = useLandscapeOverlay();
  const [searchParams, setSearchParams] = useSearchParams();

  // Select which query to use based on feed mode
  const activeQuery = isFollowingMode ? followingQuery : everyoneQuery;
  // Show loading skeleton until we have a definitive answer.
  // For the following feed, the query stays pending (no data) until the
  // contact list resolves, so treat "pending with no data" as loading.
  const isLoading =
    activeQuery.isLoading || (isFollowingMode && !activeQuery.data);

  // Flatten pages and deduplicate
  const pages = activeQuery.data?.pages;
  const moments = useMemo(() => {
    if (!pages) return [];
    const seen = new Set<string>();
    return pages.flat().filter((m) => {
      if (seen.has(m.event.id)) return false;
      seen.add(m.event.id);
      return true;
    });
  }, [pages]);

  // Infinite scroll trigger — fires ~1.5 screens before reaching the bottom
  const hasNextPage = activeQuery.hasNextPage;
  const isFetchingNextPage = activeQuery.isFetchingNextPage;
  const fetchNextPage = activeQuery.fetchNextPage;
  const { ref: loadMoreRef } = useInView({
    rootMargin: "0px 0px 150% 0px",
    onChange: (inView) => {
      if (inView && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
  });

  // Track previous user pubkey to detect login (not just presence)
  const prevPubkeyRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);

  // Show greeting after user logs in (not on page load when already logged in)
  useEffect(() => {
    const currentPubkey = user?.pubkey ?? null;

    // Skip the very first render to establish baseline
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      prevPubkeyRef.current = currentPubkey;
      return;
    }

    // Detect actual login: went from no pubkey to having one
    const justLoggedIn = currentPubkey && !prevPubkeyRef.current;

    if (justLoggedIn && !hasSeenGreeting) {
      // Small delay after login, then blink twice with greeting
      const timer = setTimeout(() => {
        setIsGreetingBlink(true);
        // First blink
        setIsBlinking(true);
        setTimeout(() => {
          setIsBlinking(false);
          // Second blink after short pause, show greeting at same time
          setTimeout(() => {
            setIsBlinking(true);
            setShowGreeting(true);
            setTimeout(() => {
              setIsBlinking(false);
              setIsGreetingBlink(false);
              // Auto-hide greeting after 2.5 seconds
              setTimeout(() => {
                setShowGreeting(false);
                setHasSeenGreeting(true);
                sessionStorage.setItem("espy-greeting-seen", "true");
              }, 2500);
            }, 250);
          }, 150);
        }, 250);
      }, 600);
      prevPubkeyRef.current = currentPubkey;
      return () => clearTimeout(timer);
    }
    prevPubkeyRef.current = currentPubkey;
  }, [user?.pubkey, hasSeenGreeting]);

  // Handle feed toggle with blink animation
  const handleFeedToggle = useCallback(() => {
    if (isBlinking) return;

    const newMode = feedMode === "everyone" ? "following" : "everyone";

    // Start blink animation (uses CSS animation, 500ms total)
    setIsBlinking(true);

    // At midpoint (250ms), switch feed and show label
    setTimeout(() => {
      setFeedMode(newMode);
      setFeedLabelText(newMode === "following" ? "friends" : "everyone");
      setShowFeedLabel(true);
    }, 250);

    // Animation complete at 500ms
    setTimeout(() => {
      setIsBlinking(false);

      // Fade label back to "espy" after a moment
      setTimeout(() => {
        setShowFeedLabel(false);
      }, 1200);
    }, 500);
  }, [feedMode, isBlinking]);

  // Track continuous scrolling for deadpan toast
  useScrollTimer();

  // Lock body scroll when overlays are open (emoji picker, camera, manual picker)
  useEffect(() => {
    if (showColorPicker || showEmojiPicker) {
      // Prevent scrolling on body
      document.body.style.overflow = "hidden";
      // Also prevent touch scrolling on iOS
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
      document.body.style.top = `-${window.scrollY}px`;
    } else {
      // Restore scrolling
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
      // Restore scroll position
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || "0", 10) * -1);
      }
    }

    return () => {
      // Cleanup on unmount
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.top = "";
    };
  }, [showColorPicker, showEmojiPicker]);

  // Show rotate prompt when emoji picker is open and user is in landscape
  useEffect(() => {
    if (showEmojiPicker) {
      const checkLandscape = () => {
        const isLandscape = window.innerWidth > window.innerHeight;
        if (isLandscape) {
          showRotatePrompt(true); // Always instant when checking
        } else {
          unsuppress();
        }
      };

      // Check immediately
      checkLandscape();

      window.addEventListener("resize", checkLandscape);
      window.addEventListener("orientationchange", checkLandscape);

      return () => {
        window.removeEventListener("resize", checkLandscape);
        window.removeEventListener("orientationchange", checkLandscape);
        unsuppress();
      };
    }
  }, [showEmojiPicker, showRotatePrompt, unsuppress]);

  // Handle camera param from landscape overlay navigation
  useEffect(() => {
    if (searchParams.get("camera") === "1" && user) {
      // Clear the param
      setSearchParams({}, { replace: true });
      // Open camera directly
      setSelectedColors([]);
      setSelectedLayout("horizontal");
      setSelectedName("");
      setSelectedEmoji("");
      setStartInCameraMode(true);
      setIsTransitioning(false);
      setShowColorPicker(true);
    }
  }, [searchParams, setSearchParams, user]);

  // Handle openCamera event from landscape overlay on same page
  useEffect(() => {
    const handleOpenCamera = () => {
      if (user) {
        setSelectedColors([]);
        setSelectedLayout("horizontal");
        setSelectedName("");
        setSelectedEmoji("");
        setStartInCameraMode(true);
        setIsTransitioning(false);
        setShowColorPicker(true);
      }
    };

    window.addEventListener("openCamera", handleOpenCamera);
    return () => window.removeEventListener("openCamera", handleOpenCamera);
  }, [user]);

  // Continuous water wiggle animation
  useEffect(() => {
    if (isPulling) {
      const animate = () => {
        setWaterMotion((prev) => prev + 0.08);
        waterAnimationRef.current = requestAnimationFrame(animate);
      };
      waterAnimationRef.current = requestAnimationFrame(animate);
    } else {
      if (waterAnimationRef.current) {
        cancelAnimationFrame(waterAnimationRef.current);
        waterAnimationRef.current = null;
      }
      setWaterMotion(0);
    }
    return () => {
      if (waterAnimationRef.current) {
        cancelAnimationFrame(waterAnimationRef.current);
      }
    };
  }, [isPulling]);

  // Note: overscroll-behavior is set globally in index.css to prevent iOS Safari issues

  // Pull to refresh handlers with elastic rubber band effect
  const handleTouchStart = (e: React.TouchEvent) => {
    // Only start pull if at top of page
    if (window.scrollY === 0) {
      touchStartY.current = e.touches[0].clientY;
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartY.current === 0 || window.scrollY > 0) return;

    const touchY = e.touches[0].clientY;
    const distance = touchY - touchStartY.current;

    // Only track downward pulls with elastic resistance
    if (distance > 0) {
      setIsPulling(true);
      // Elastic resistance formula: diminishing returns as you pull further
      const elasticDistance = distance * (1 - Math.min(distance / 400, 0.7));
      setPullDistance(Math.min(elasticDistance, 180)); // Max 180px
    }
  };

  const handleTouchEnd = async () => {
    if (pullDistance > 60 && !isRefreshing) {
      setIsRefreshing(true);
      // Snap to refresh position
      setPullDistance(80);
      // Invalidate queries to refetch
      await queryClient.invalidateQueries({
        queryKey: ["color-moments-infinite"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["following-moments-infinite"],
      });
      setTimeout(() => {
        setIsRefreshing(false);
        setIsPulling(false);
        setPullDistance(0);
      }, 800);
    } else {
      // Elastic snap back
      setIsPulling(false);
      setPullDistance(0);
    }
    touchStartY.current = 0;
  };

  const handleOpenColorPicker = () => {
    if (buttonRef.current) {
      setButtonRect(buttonRef.current.getBoundingClientRect());
    }
    // Clear any previous colors for fresh start
    setSelectedColors([]);
    setSelectedLayout("horizontal");
    setSelectedName("");
    setSelectedEmoji("");
    setStartInCameraMode(false);
    setIsTransitioning(true);
    setShowColorPicker(true);
  };

  const handleTransitionComplete = useCallback(() => {
    // Transition complete - content is now fully visible
  }, []);

  const handleCancelCapture = (rect?: DOMRect) => {
    // Close capture screen and trigger global transition
    setShowColorPicker(false);
    setIsTransitioning(false);
    // Play the transition animation (no navigation needed, we're already on the feed)
    playTransition(rect);
  };

  const handleColorsSelected = (colors: string[]) => {
    // Store colors and trigger curtain transition
    setPendingColors(colors);
    setShowCurtainTransition(true);
  };

  const handleCurtainMidpoint = () => {
    // At midpoint (curtains fully closed), switch screens
    setSelectedColors(pendingColors);
    // If in landscape, show rotate prompt instantly before showing emoji picker
    const isLandscape = window.innerWidth > window.innerHeight;
    if (isLandscape) {
      showRotatePrompt(true); // instant - no fade
    }
    setIsTransitioning(false);
    setShowColorPicker(false);
    setShowEmojiPicker(true);
  };

  const handleCurtainComplete = () => {
    setShowCurtainTransition(false);
    setPendingColors([]);
  };

  const handleBackToColorPicker = () => {
    // Trigger curtain transition to go back
    setShowCurtainBack(true);
  };

  const handleCurtainBackMidpoint = () => {
    // At midpoint, switch back to color picker
    setShowEmojiPicker(false);
    setShowColorPicker(true);
    setIsTransitioning(false);
  };

  const handleCurtainBackComplete = () => {
    setShowCurtainBack(false);
  };

  const handleRemix = (colors: string[], rect: DOMRect) => {
    setSelectedColors(colors);
    setButtonRect(rect);
    // Go to color palette with ink animation
    setIsTransitioning(true);
    setShowColorPicker(true);
  };

  const handleEmojiSelected = (emoji: string) => {
    setSelectedEmoji(emoji);
  };

  const handleClearEmoji = () => {
    setSelectedEmoji("");
  };

  const handleCopyColors = () => {
    const colorList = formatColorsForCopy(selectedColors);
    navigator.clipboard.writeText(colorList);
    toast({ title: getColorCopyToast() });
  };

  const handleSubmit = () => {
    if (!user) return;

    // Capture current values for the event
    const colors = [...selectedColors];
    const layout = selectedLayout;
    const name = selectedName.trim();
    const emoji = selectedEmoji;

    // Keep emoji picker open but don't close anything - loading state on button is enough feedback
    // User can see the palette and emoji while the button shows "Signing..."

    const tags: string[][] = [
      ...colors.map((color) => ["c", color]),
      ["layout", layout],
      ["alt", `Color moment: ${colors.join(", ")}`],
    ];
    if (name) {
      tags.push(["name", name]);
    }

    createEvent(
      {
        kind: 3367,
        content: emoji,
        tags,
      },
      {
        onSuccess: () => {
          // Close emoji picker and trigger celebration AFTER successful signing
          setShowEmojiPicker(false);
          setConfettiColors(
            colors.length > 0
              ? colors
              : ["#f43f5e", "#f59e0b", "#10b981", "#3b82f6"],
          );
          setShowConfetti(true);
          // Gate: single roll decides which easter egg fires
          const submittedColors = colors.length > 0 ? colors : undefined;
          if (submittedColors) {
            const event = rollPostEvent(submittedColors);
            if (event === "critic") {
              setCriticColors(submittedColors);
            } else if (event === "alchemist") {
              setAlchemistColors(submittedColors);
            } else if (event === "astrologer") {
              setAstrologerEmoji(emoji || "");
              setAstrologerColors(submittedColors);
            }
          }
          setSelectedColors([]);
          setSelectedLayout("horizontal");
          setSelectedName("");
          setSelectedEmoji("");

          if (emoji) {
            const toastMessage = getEmojiToast(emoji, "vibe");
            toast({ title: `${emoji} ${toastMessage}` });
          } else {
            toast({ title: "✨ Shared!" });
          }
          queryClient.invalidateQueries({
            queryKey: ["color-moments-infinite"],
          });
        },
        onError: (error: Error) => {
          toast({
            title: "Signing failed",
            description: `${error.message}\n\n${error.stack?.split("\n").slice(0, 3).join("\n") || ""}`,
            variant: "destructive",
          });
        },
      },
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-200 via-amber-100 via-emerald-100 to-sky-200 dark:from-rose-950 dark:via-violet-950 dark:to-sky-950">
      {/* Confetti celebration */}
      <Confetti
        colors={confettiColors}
        active={showConfetti}
        onComplete={() => setShowConfetti(false)}
      />

      {/* Tea Time experience - triggers after 3 minutes of idle on the main feed, once per week */}
      <TeaTimeOverlay
        enabled={!!user}
        idleTimeout={180000}
        cooldown={7 * 24 * 60 * 60 * 1000}
        moments={userMoments}
        username={userAuthor.data?.metadata?.name}
      />

      {/* Color Critic - Shakespeare reviews your latest color moment */}
      {user && !showColorPicker && !showEmojiPicker && (
        <ColorCritic
          latestMoment={userMoments?.[0]}
          justSubmittedColors={criticColors}
          onDismiss={() => setCriticColors(undefined)}
        />
      )}

      {/* Color Alchemist - mystical tarot reading of your palette */}
      {user && !showColorPicker && !showEmojiPicker && (
        <ColorAlchemist
          justSubmittedColors={alchemistColors}
          onDismiss={() => setAlchemistColors(undefined)}
        />
      )}

      {/* Color Astrologer - star chart reading of your emoji */}
      {user && !showColorPicker && !showEmojiPicker && (
        <ColorAstrologer
          justSubmittedEmoji={astrologerEmoji}
          justSubmittedColors={astrologerColors}
          onDismiss={() => {
            setAstrologerEmoji(undefined);
            setAstrologerColors(undefined);
          }}
        />
      )}

      {/* Curtain transition between palette and emoji picker */}
      <CurtainTransition
        isActive={showCurtainTransition}
        onMidpoint={handleCurtainMidpoint}
        onComplete={handleCurtainComplete}
        colors={pendingColors}
      />

      {/* Curtain transition for going back */}
      <CurtainTransition
        isActive={showCurtainBack}
        onMidpoint={handleCurtainBackMidpoint}
        onComplete={handleCurtainBackComplete}
        colors={selectedColors}
      />

      {/* Header */}
      <header className="sticky top-0 z-40 bg-background border-b border-border shadow-sm safe-top-mobile">
        <div className="max-w-xl mx-auto px-5 py-4 flex items-center justify-between">
          {/* Logo - tappable when logged in to toggle feed */}
          <div className="relative">
            {user ? (
              <button
                onClick={handleFeedToggle}
                disabled={isBlinking}
                className="flex items-center gap-2.5 hover:opacity-80 active:scale-95 transition-all disabled:pointer-events-none"
              >
                {/* Blinking eye logo */}
                <div className="relative w-9 h-9 rounded-full overflow-hidden">
                  <img src="/favicon.svg" alt="Espy" className="w-9 h-9" />
                  {/* Eyelid blink overlay - matches color picker style with lashes */}
                  {isBlinking && (
                    <div
                      className={`absolute inset-0 ${isGreetingBlink ? "animate-eyelid-blink-fast" : "animate-eyelid-blink"}`}
                      style={{ top: 0, width: "100%", height: "100%" }}
                    >
                      {/* Flat eyelid matching nav bar background */}
                      <div
                        className="absolute rounded-full bg-background"
                        style={{
                          width: "100%",
                          height: "100%",
                          bottom: 0,
                          left: 0,
                        }}
                      />
                      {/* Eyelashes icon */}
                      <EyeClosed
                        className="absolute w-full h-auto text-muted-foreground/60"
                        style={{
                          top: "100%",
                          transform: "translateY(-60%)",
                        }}
                        strokeWidth={1.5}
                      />
                    </div>
                  )}
                </div>
                {/* Animated text: espy <-> friends/everyone */}
                <div className="relative h-7 flex items-center">
                  <span
                    className="text-xl font-medium tracking-tight text-foreground transition-all duration-300"
                    style={{
                      opacity: showFeedLabel ? 0 : 1,
                      transform: showFeedLabel
                        ? "translateY(-8px)"
                        : "translateY(0)",
                    }}
                  >
                    espy
                  </span>
                  <span
                    className="absolute left-0 text-xl font-medium tracking-tight transition-all duration-300"
                    style={{
                      opacity: showFeedLabel ? 1 : 0,
                      transform: showFeedLabel
                        ? "translateY(0)"
                        : "translateY(8px)",
                      color:
                        feedLabelText === "friends" ? "#a78bfa" : "#f59e0b",
                    }}
                  >
                    {feedLabelText}
                  </span>
                </div>
              </button>
            ) : (
              <div className="flex items-center gap-2.5">
                <img src="/favicon.svg" alt="Espy" className="w-9 h-9" />
                <span className="text-xl font-medium tracking-tight text-foreground">
                  espy
                </span>
              </div>
            )}

            {/* Greeting speech bubble - shows after login */}
            {showGreeting && (
              <div className="absolute top-full left-2 mt-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                <div className="bg-amber-50 text-amber-900 text-sm font-medium px-2.5 py-1 rounded-full shadow-lg whitespace-nowrap border border-amber-200">
                  {greeting}
                </div>
                {/* Arrow pointing up */}
                <div className="absolute -top-1 left-3 w-2 h-2 bg-amber-50 rotate-45 border-l border-t border-amber-200" />
              </div>
            )}
          </div>

          <LoginArea className="max-w-60" />
        </div>
      </header>

      {/* Pull to refresh wavy ink puddle indicator - above feed, under nav */}
      {isPulling && (
        <div
          className="fixed top-[68px] left-0 right-0 pointer-events-none z-30 overflow-visible"
          style={{
            height: `${Math.min(pullDistance * 1.2, 200)}px`,
          }}
        >
          {/* SVG ink puddles that barely peek under nav */}
          <svg
            className="absolute top-0 left-0 w-full overflow-visible"
            style={{
              height: `${Math.min(pullDistance * 1.2, 200)}px`,
              opacity: Math.min(pullDistance / 40, 0.9),
            }}
          >
            {/* Generate organic ink blobs for each color */}
            {[
              { color: "#f87171", offset: 0 }, // rose
              { color: "#fbbf24", offset: 0.15 }, // amber
              { color: "#34d399", offset: 0.3 }, // emerald
              { color: "#60a5fa", offset: 0.45 }, // sky
              { color: "#a78bfa", offset: 0.6 }, // violet
              { color: "#fb923c", offset: 0.75 }, // orange
            ].map((blob, i) => {
              // Generate organic blob path with wider blobs and continuous water motion
              const generateBlobPath = () => {
                const centerX = (window.innerWidth / 5) * (i + 0.3); // Wider spacing
                const centerY = -20 + pullDistance * 0.5;
                const baseRadius = 80 + pullDistance * 0.4; // Much wider blobs
                const points: [number, number][] = [];
                const segments = 48; // More segments for smoother curves

                for (let j = 0; j < segments; j++) {
                  const angle = (j / segments) * Math.PI * 2;
                  // Water wiggle motion - continuous animation
                  const wiggle1 =
                    Math.sin(angle * 3 + waterMotion + i * 0.5) * 0.25;
                  const wiggle2 =
                    Math.sin(angle * 7 + waterMotion * 1.3 + i * 0.8) * 0.18;
                  const wiggle3 =
                    Math.sin(angle * 5 + waterMotion * 0.7 + i * 1.2) * 0.12;
                  const wiggle4 =
                    Math.cos(angle * 9 + waterMotion * 1.5) * 0.08;
                  const noiseTotal = 1 + wiggle1 + wiggle2 + wiggle3 + wiggle4;
                  const r = baseRadius * noiseTotal;

                  // Add vertical water-like undulation
                  const verticalWave =
                    Math.sin(waterMotion * 2 + i * 0.7) * 0.15;

                  points.push([
                    centerX + r * Math.cos(angle),
                    centerY + r * Math.sin(angle) * (0.85 + verticalWave),
                  ]);
                }

                // Smooth bezier curves through all points
                let path = `M ${points[0][0]} ${points[0][1]}`;
                for (let j = 0; j < points.length; j++) {
                  const p1 = points[j];
                  const p2 = points[(j + 1) % points.length];
                  const cp1x = p1[0] + (p2[0] - p1[0]) * 0.35;
                  const cp1y = p1[1] + (p2[1] - p1[1]) * 0.35;
                  const cp2x = p2[0] - (p2[0] - p1[0]) * 0.35;
                  const cp2y = p2[1] - (p2[1] - p1[1]) * 0.35;
                  path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2[0]} ${p2[1]}`;
                }
                path += " Z";
                return path;
              };

              return (
                <path
                  key={i}
                  d={generateBlobPath()}
                  fill={blob.color}
                  opacity={0.75}
                  style={{
                    filter: "blur(3px)",
                    transition:
                      isRefreshing || !isPulling
                        ? "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)"
                        : "none",
                  }}
                />
              );
            })}
          </svg>
        </div>
      )}

      {/* Emoji indicator - ABOVE nav bar */}
      {isPulling && (
        <div
          className="fixed left-1/2 -translate-x-1/2 z-50 pointer-events-none"
          style={{
            top: `${68 - 20 + Math.min(pullDistance * 0.5, 80)}px`,
            transform: `translateX(-50%) scale(${Math.min(pullDistance / 50, 1.5)}) rotate(${Math.sin(waterMotion * 0.5) * 8}deg)`,
            transition:
              isRefreshing || !isPulling
                ? "all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)"
                : "none",
          }}
        >
          <div className="text-5xl drop-shadow-2xl filter">
            {isRefreshing ? "⏳" : pullDistance > 60 ? "✨" : "👇"}
          </div>
        </div>
      )}

      {/* Main - drags down with elastic effect */}
      <main
        className="max-w-xl mx-auto px-5 pb-32 safe-bottom relative z-10"
        style={{
          transform: `translateY(${isPulling ? pullDistance : 0}px)`,
          transition: isPulling
            ? "none"
            : "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Welcome */}
        {!user && (
          <div className="py-16 text-center space-y-6">
            {/* Friendly rainbow blob */}
            <div className="relative w-32 h-32 mx-auto">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-rose-300 via-amber-200 to-sky-300 animate-pulse" />
              <div className="absolute inset-4 rounded-full bg-gradient-to-tr from-amber-200 via-rose-200 to-violet-200" />
              <div className="absolute inset-0 flex items-center justify-center text-5xl">
                👁️
              </div>
            </div>

            <div className="space-y-3">
              <h1 className="text-3xl font-semibold">espy</h1>
              <p className="text-muted-foreground max-w-xs mx-auto text-lg leading-relaxed">
                See something beautiful? Share its colors with the world.
              </p>
            </div>
          </div>
        )}

        {/* Feed */}
        <div className="space-y-10 pt-4 pb-32">
          {isLoading ? (
            <>
              {[1, 2, 3].map((i) => (
                <div key={i} className="space-y-4">
                  <div className="aspect-square rounded-3xl overflow-hidden flex flex-col">
                    {[1, 2, 3, 4].map((j) => (
                      <Skeleton key={j} className="flex-1 rounded-none" />
                    ))}
                  </div>
                  <div className="flex gap-3 px-2">
                    {[1, 2, 3].map((j) => (
                      <Skeleton key={j} className="h-9 w-9 rounded-full" />
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : moments.length > 0 ? (
            <>
              {moments.map((moment) => (
                <ColorMomentCard
                  key={moment.event.id}
                  moment={moment}
                  onRemix={user ? handleRemix : undefined}
                />
              ))}

              {/* Infinite scroll sentinel */}
              {activeQuery.hasNextPage && (
                <div ref={loadMoreRef} className="py-4">
                  {activeQuery.isFetchingNextPage && (
                    <div className="space-y-4">
                      <div className="aspect-square rounded-3xl overflow-hidden flex flex-col">
                        {[1, 2, 3, 4].map((j) => (
                          <Skeleton key={j} className="flex-1 rounded-none" />
                        ))}
                      </div>
                      <div className="flex gap-3 px-2">
                        {[1, 2, 3].map((j) => (
                          <Skeleton key={j} className="h-9 w-9 rounded-full" />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="py-20 text-center space-y-6">
              {/* Empty state - different for following vs everyone */}
              <div className="w-40 h-40 mx-auto rounded-3xl bg-gradient-to-br from-rose-200 via-amber-100 to-sky-200 flex items-center justify-center">
                <span className="text-6xl">
                  {feedMode === "following" ? "👀" : "🌈"}
                </span>
              </div>
              <div className="space-y-3">
                {feedMode === "following" && user ? (
                  <>
                    <p className="text-xl text-muted-foreground">
                      No colors from your circle yet
                    </p>
                    <p className="text-sm text-muted-foreground/70 max-w-xs mx-auto">
                      Follow more color seekers or tap the eye to see everyone's
                      moments
                    </p>
                    <button
                      onClick={() => setFeedMode("everyone")}
                      className="text-amber-600 hover:text-amber-700 hover:underline font-medium text-sm"
                    >
                      See everyone's colors →
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-xl text-muted-foreground">
                      No colors yet
                    </p>
                    {user && (
                      <button
                        onClick={handleOpenColorPicker}
                        className="text-primary hover:underline font-medium"
                      >
                        Be the first to share!
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Big friendly capture button */}
      {user && !showColorPicker && (
        <button
          ref={buttonRef}
          onClick={handleOpenColorPicker}
          className="fixed bottom-10 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-auto z-40 px-6 py-4 rounded-full bg-gradient-to-r from-rose-400 via-amber-400 to-emerald-400 text-white font-medium text-lg shadow-xl shadow-amber-300/40 dark:shadow-amber-900/40 hover:scale-105 active:scale-95 transition-transform flex items-center justify-center gap-2"
        >
          <span className="text-2xl">👁️</span>
          <span>I see something</span>
        </button>
      )}

      {/* Capture Modal with Wavy Transition */}
      {showColorPicker &&
        user &&
        (isTransitioning ? (
          <WavyTransition
            isActive={isTransitioning}
            onTransitionComplete={handleTransitionComplete}
            buttonRect={buttonRect}
          >
            <CaptureModeSelector
              onComplete={handleColorsSelected}
              onCancel={handleCancelCapture}
              initialColors={
                selectedColors.length > 0 ? selectedColors : undefined
              }
              startInCameraMode={startInCameraMode}
            />
          </WavyTransition>
        ) : (
          <CaptureModeSelector
            onComplete={handleColorsSelected}
            onCancel={handleCancelCapture}
            initialColors={
              selectedColors.length > 0 ? selectedColors : undefined
            }
            startInCameraMode={startInCameraMode}
          />
        ))}

      {/* Emoji Modal */}
      {showEmojiPicker && user && (
        <div className="fixed inset-0 z-50 bg-gradient-to-br from-rose-200 via-amber-100 via-emerald-100 to-sky-200 dark:from-rose-950 dark:via-violet-950 dark:to-sky-950 flex flex-col">
          {/* Header */}
          <div
            className="max-w-xl mx-auto w-full px-5 pb-4 safe-top-mobile"
            style={{ "--safe-top-min": "1.5rem" } as React.CSSProperties}
          >
            <button
              onClick={handleBackToColorPicker}
              disabled={isPublishing}
              className="p-2 -ml-2 rounded-full text-foreground/60 hover:text-foreground hover:bg-black/5 dark:hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          </div>

          {/* Content - scrollable */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            <div className="flex flex-col items-center px-6 pt-2 pb-32">
              <div className="w-full max-w-sm space-y-6">
                {/* Name field */}
                <input
                  type="text"
                  value={selectedName}
                  onChange={(e) => setSelectedName(e.target.value)}
                  placeholder="Name this moment..."
                  disabled={isPublishing}
                  className="w-full px-4 py-3 rounded-2xl bg-white/60 dark:bg-white/10 border-0 text-center text-lg placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-foreground/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  maxLength={50}
                />

                {/* Preview with layout - layout picker inside */}
                <ColorPaletteDisplay
                  colors={selectedColors}
                  layout={selectedLayout}
                  className="aspect-square rounded-3xl overflow-hidden shadow-2xl shadow-black/10"
                >
                  {/* Selected emoji in center */}
                  {selectedEmoji && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                      <span className="text-6xl drop-shadow-lg">
                        {selectedEmoji}
                      </span>
                    </div>
                  )}

                  {/* Layout toggle and copy buttons - bottom left, same as feed view */}
                  {(() => {
                    const availableLayouts = getAvailableLayouts(
                      selectedColors.length,
                    );
                    const LayoutIcon = LAYOUT_ICONS[selectedLayout];
                    return (
                      <div className="absolute bottom-3 left-3 flex items-center gap-2 z-20">
                        <button
                          onClick={() => {
                            const currentIndex =
                              availableLayouts.indexOf(selectedLayout);
                            const nextIndex =
                              (currentIndex + 1) % availableLayouts.length;
                            setSelectedLayout(availableLayouts[nextIndex]);
                          }}
                          disabled={isPublishing}
                          className="p-2.5 rounded-full bg-black/20 text-white/80 hover:bg-black/30 hover:text-white active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-black/20"
                          title="Change layout"
                        >
                          <LayoutIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={handleCopyColors}
                          disabled={isPublishing}
                          className="p-2.5 rounded-full bg-black/20 text-white/80 hover:bg-black/30 hover:text-white active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-black/20"
                          title="Copy colors"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })()}
                </ColorPaletteDisplay>

                <p className="text-center text-xl text-muted-foreground">
                  How does it feel?
                </p>

                <EmojiPicker
                  onSelect={handleEmojiSelected}
                  maxHeight={220}
                  disabled={isPublishing}
                />

                {/* "just the colors" - only shows after emoji selected, clears emoji */}
                {selectedEmoji && (
                  <button
                    onClick={handleClearEmoji}
                    disabled={isPublishing}
                    className="w-full py-4 text-muted-foreground hover:text-foreground transition-colors text-lg disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    just the colors
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Ready button - fixed at bottom, similar to "I see something" */}
          <button
            onClick={handleSubmit}
            disabled={isPublishing}
            className="fixed bottom-10 left-4 right-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-auto z-50 px-6 py-4 rounded-full bg-gradient-to-r from-rose-400 via-amber-400 to-emerald-400 text-white font-medium text-lg shadow-xl shadow-amber-300/40 dark:shadow-amber-900/40 hover:scale-105 active:scale-95 transition-transform flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {isPublishing ? (
              <>
                <svg
                  className="animate-spin h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span>Signing...</span>
              </>
            ) : (
              <>
                {selectedEmoji && (
                  <span className="text-2xl">{selectedEmoji}</span>
                )}
                <span>Ready?</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default Index;
